/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  BookOpen, Users, MessageSquare, Plus, Edit, Trash2, ChevronUp, ChevronDown,
  Upload, X, Search, MoreVertical, ExternalLink, UserCog, KeyRound, UserCircle,
  UserPlus, StickyNote, Send, Clock,
} from 'lucide-react';
import { MobilePhotoUploadButton } from '../../components/MobilePhotoCropModal';
import { notifyError, notifySuccess, notifyInfo } from '../../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const fmtDate = (iso, withTime = false) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'America/Chicago'});
  const day = String(d.getUTCDate()).padStart(2, '0');
  const year = d.getUTCFullYear();
  if (!withTime) return `${month} ${day} ${year}`;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${month} ${day} ${year} ${hh}:${mm} UTC`;
};

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const openUploadWidget = (resourceType, onDone) => {
  if (!window.cloudinary || !CLOUD_NAME || !UPLOAD_PRESET) {
    notifyError('Cloudinary is not configured.');
    return;
  }
  const isVideo = resourceType === 'video';
  // iter 116 - for images, allow every Cloudinary upload source the unsigned
  // preset supports so admins on mobile can pick from their device photo
  // library (`local`), take a fresh photo (`camera`), or search the
  // Unsplash-style hosted library (`image_search`). For videos we keep the
  // narrower set since `image_search` is image-only and `camera` on mobile
  // can't shoot a 500 MB clip cleanly.
  const sources = isVideo
    ? ['local', 'url']
    : ['local', 'url', 'camera', 'image_search'];
  const w = window.cloudinary.createUploadWidget(
    {
      cloudName: CLOUD_NAME,
      uploadPreset: UPLOAD_PRESET,
      sources,
      multiple: false,
      resourceType,
      folder: isVideo ? 'frg_classroom_videos' : 'frg_classroom_thumbs',
      // Lesson videos: allow up to 500 MB. Lesson thumbnails: 5 MB.
      maxFileSize: isVideo ? 500_000_000 : 5_000_000,
      // Cloudinary's chunked-upload path - required for files >100 MB.
      // 20 MB chunks; only applied to videos so thumbnails stay on the
      // simple single-request path.
      ...(isVideo
        ? { chunkSize: 20_000_000, maxVideoFileSize: 500_000_000 }
        : {}),
      // Mobile-friendly UI tweaks (iter 116).
      showAdvancedOptions: false,
      showPoweredBy: false,
      defaultSource: 'local',
    },
    (err, result) => {
      if (!err && result && result.event === 'success') {
        onDone(result.info.secure_url);
        w.close();
      }
    }
  );
  w.open();
};

const Modal = ({ title, onClose, children, testId }) => (
  <div
    className="fixed inset-0 z-50 bg-black/60 flex items-stretch md:items-center justify-center md:p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="bg-[#FFFFFF] md:border border-[#F4ECD8] w-full md:max-w-lg h-full md:h-auto md:max-h-[90vh] overflow-y-auto flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-4 border-b border-[#F4ECD8] sticky top-0 bg-[#FFFFFF] z-10">
        <h3 className="text-[#0A1F2E] font-medium">{title}</h3>
        <button onClick={onClose} className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4 flex-1">{children}</div>
    </div>
  </div>
);

const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
    <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-6 max-w-md">
      <p className="text-[#0A1F2E] text-sm mb-5">{message}</p>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-[#0A1F2E]/60 hover:text-[#0A1F2E]">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-btn">Delete</button>
      </div>
    </div>
  </div>
);

// ============== MODULES TAB ==============

const ModulesTab = () => {
  const [modules, setModules] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState(null); // module object or 'new'
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/admin/community/modules`, { credentials: 'include' });
    if (r.ok) setModules((await r.json()).modules || []);
  }, []);
  useEffect(() => { load(); loadCloudinaryScript(); }, [load]);

  const handleDelete = async (module_id) => {
    await fetch(`${API}/api/admin/community/modules/${module_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    setDeleting(null);
    load();
  };

  return (
    <div data-testid="admin-modules-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl text-white font-light">Modules</h2>
        <button
          onClick={() => setEditing('new')}
          className="bg-[#2CA8A0] text-black px-4 py-2 text-sm font-semibold hover:bg-[#2CA8A0] inline-flex items-center gap-1"
          data-testid="add-module-btn"
        >
          <Plus className="w-4 h-4" /> Add New Module
        </button>
      </div>

      <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-x-auto">
        <table className="w-full text-sm" data-testid="modules-table">
          <thead>
            <tr className="text-left text-[#0A1F2E]/60 text-xs uppercase tracking-wide border-b border-[#F4ECD8]">
              <th className="p-3"></th>
              <th className="p-3">Thumbnail</th>
              <th className="p-3">Title</th>
              <th className="p-3">Lessons</th>
              <th className="p-3">Status</th>
              <th className="p-3">Created</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <React.Fragment key={m.module_id}>
                <tr className="border-b border-[#F4ECD8] hover:bg-[#FFFFFF]">
                  <td className="p-3">
                    <button onClick={() => setExpandedId(expandedId === m.module_id ? null : m.module_id)}>
                      {expandedId === m.module_id
                        ? <ChevronUp className="w-4 h-4 text-[#888]" />
                        : <ChevronDown className="w-4 h-4 text-[#888]" />}
                    </button>
                  </td>
                  <td className="p-3">
                    {m.thumbnail_url ? (
                      <img src={m.thumbnail_url} alt="" className="w-[50px] h-[50px] object-cover" />
                    ) : <div className="w-[50px] h-[50px] bg-[#F4ECD8]" />}
                  </td>
                  <td className="p-3 text-white">{m.title}</td>
                  <td className="p-3 text-[#ccc]">{(m.lessons || []).length}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium ${
                      m.status === 'published'
                        ? 'bg-green-600/20 text-green-400'
                        : 'bg-[#333] text-[#aaa]'
                    }`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="p-3 text-[#888]">{fmtDate(m.created_at)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing(m)} className="text-[#2CA8A0] hover:underline text-xs"
                              data-testid={`edit-module-${m.module_id}`}>
                        Edit
                      </button>
                      <button onClick={() => setDeleting(m)} className="text-red-400 hover:underline text-xs"
                              data-testid={`delete-module-${m.module_id}`}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === m.module_id && (
                  <tr><td colSpan={7} className="p-0 bg-[#0f0f0f]">
                    <LessonsTable module={m} onRefresh={load} />
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {modules.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#666]">No modules yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ModuleForm
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          message="Are you sure you want to delete this module? This cannot be undone."
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting.module_id)}
        />
      )}
    </div>
  );
};

const ModuleForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    thumbnail_url: initial?.thumbnail_url || '',
    status: initial?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);

  // iter 116 - ensure the Cloudinary widget script is loaded before the
  // user taps the upload button. Without this the thumbnail upload silently
  // falls back to a camera-only mobile capture input.
  useEffect(() => { loadCloudinaryScript(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const isNew = !initial;
    const url = isNew
      ? `${API}/api/admin/community/modules`
      : `${API}/api/admin/community/modules/${initial.module_id}`;
    const r = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (r.ok) onSaved();
    else notifyError('Save failed.');
  };

  return (
    <Modal title={initial ? 'Edit module' : 'Add new module'} onClose={onClose} testId="module-form-modal">
      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Title *</label>
          <input required value={form.title}
                 onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                 className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm"
                 data-testid="module-title-input" />
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Description * <span className="text-[#555]">({form.description.length}/300)</span></label>
          <textarea required maxLength={300} rows={3} value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value.slice(0, 300) }))}
                    className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm resize-none"
                    data-testid="module-description-input" />
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Thumbnail *</label>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            {form.thumbnail_url && (
              <img
                src={form.thumbnail_url}
                alt=""
                className="w-16 h-16 object-cover rounded border border-[#F4ECD8]"
              />
            )}
            <button
              type="button"
              onClick={() =>
                openUploadWidget('image', (url) =>
                  setForm((p) => ({ ...p, thumbnail_url: url }))
                )
              }
              className="inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-[#2CA8A0] text-white text-sm font-semibold rounded hover:brightness-95 active:brightness-90 transition flex-1 sm:flex-none"
              data-testid="module-upload-thumb"
            >
              <Upload className="w-4 h-4" />
              <span>{form.thumbnail_url ? 'Replace thumbnail' : 'Upload thumbnail'}</span>
            </button>
          </div>
          <p className="mt-2 text-[#0A1F2E]/50 text-[11px]">
            Choose from your device photo library, take a new photo, paste a URL,
            or pick from the Cloudinary image search.
          </p>
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Status</label>
          <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm"
                  data-testid="module-status-select">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
          <button type="submit" disabled={saving || !form.thumbnail_url}
                  className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold hover:bg-[#2CA8A0] disabled:opacity-50"
                  data-testid="module-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============== LESSONS (nested inside a module) ==============

const LessonsTable = ({ module, onRefresh }) => {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const lessons = [...(module.lessons || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const reorder = async (lesson_id, direction) => {
    await fetch(`${API}/api/admin/community/modules/${module.module_id}/lessons/${lesson_id}/reorder`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ direction }),
    });
    onRefresh();
  };

  const handleDelete = async (lesson_id) => {
    await fetch(`${API}/api/admin/community/modules/${module.module_id}/lessons/${lesson_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    setDeleting(null);
    onRefresh();
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-white text-sm">Lessons in {module.title}</h4>
        <button onClick={() => setEditing('new')} className="bg-[#2CA8A0] text-black px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
                data-testid={`add-lesson-${module.module_id}`}>
          <Plus className="w-3 h-3" /> Add New Lesson
        </button>
      </div>
      <table className="w-full text-sm" data-testid="lessons-table">
        <thead>
          <tr className="text-left text-[#0A1F2E]/60 text-xs uppercase tracking-wide border-b border-[#F4ECD8]">
            <th className="p-2">Order</th>
            <th className="p-2">Title</th>
            <th className="p-2">Video</th>
            <th className="p-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {lessons.map((l, i) => (
            <tr key={l.lesson_id} className="border-b border-[#F4ECD8]">
              <td className="p-2 text-[#ccc]">{l.order}</td>
              <td className="p-2 text-white">{l.title}</td>
              <td className="p-2">
                {l.cloudinary_video_url ? (
                  <a href={l.cloudinary_video_url} target="_blank" rel="noreferrer"
                     className="text-[#2CA8A0] text-xs inline-flex items-center gap-1 hover:underline">
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                ) : '-'}
              </td>
              <td className="p-2">
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => reorder(l.lesson_id, 'up')} disabled={i === 0}
                          className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] disabled:opacity-30">
                    <ChevronUp className="w-3 h-3" />
                  </button>
                  <button onClick={() => reorder(l.lesson_id, 'down')} disabled={i === lessons.length - 1}
                          className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] disabled:opacity-30">
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  <button onClick={() => setEditing(l)} className="text-[#2CA8A0] text-xs hover:underline">Edit</button>
                  <button onClick={() => setDeleting(l)} className="text-red-400 text-xs hover:underline">Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {lessons.length === 0 && (
            <tr><td colSpan={4} className="p-4 text-center text-[#666]">No lessons in this module yet.</td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <LessonForm
          moduleId={module.module_id}
          initial={editing === 'new' ? null : editing}
          defaultOrder={lessons.length + 1}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onRefresh(); }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          message={`Delete lesson "${deleting.title}"? This also removes all progress records.`}
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting.lesson_id)}
        />
      )}
    </div>
  );
};

const LessonForm = ({ moduleId, initial, defaultOrder, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    cloudinary_video_url: initial?.cloudinary_video_url || '',
    thumbnail_url: initial?.thumbnail_url || '',
    order: initial?.order ?? defaultOrder,
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const isNew = !initial;
    const url = isNew
      ? `${API}/api/admin/community/modules/${moduleId}/lessons`
      : `${API}/api/admin/community/modules/${moduleId}/lessons/${initial.lesson_id}`;
    const r = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...form,
        thumbnail_url: form.thumbnail_url ? form.thumbnail_url : null,
      }),
    });
    setSaving(false);
    if (r.ok) onSaved();
    else notifyError('Save failed.');
  };
  return (
    <Modal title={initial ? 'Edit lesson' : 'Add new lesson'} onClose={onClose} testId="lesson-form-modal">
      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Title *</label>
          <input required value={form.title}
                 onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                 className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm"
                 data-testid="lesson-title-input" />
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Description *</label>
          <textarea required rows={3} value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm resize-none"
                    data-testid="lesson-description-input" />
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Video *</label>
          <div className="flex items-center gap-3 mt-1">
            {form.cloudinary_video_url && <span className="text-[#2CA8A0] text-xs truncate max-w-[200px]">{form.cloudinary_video_url.split('/').pop()}</span>}
            <button type="button"
                    onClick={() => openUploadWidget('video', (url) => setForm((p) => ({ ...p, cloudinary_video_url: url })))}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-[#F4ECD8] text-[#0A1F2E] text-sm hover:bg-[#F9F6EF]"
                    data-testid="lesson-upload-video">
              <Upload className="w-4 h-4" /> {form.cloudinary_video_url ? 'Replace' : 'Upload video'}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Thumbnail (optional)</label>
          <div className="flex items-center gap-3 mt-1">
            {form.thumbnail_url ? (
              <div className="relative w-20 h-20 flex-shrink-0">
                <img
                  src={form.thumbnail_url}
                  alt="Lesson thumbnail preview"
                  className="w-full h-full object-cover rounded border border-[#F4ECD8]"
                  data-testid="lesson-thumbnail-preview"
                />
              </div>
            ) : (
              <div className="w-20 h-20 flex-shrink-0 rounded border border-dashed border-[#F4ECD8] flex items-center justify-center text-[#888] text-[10px]">
                None
              </div>
            )}
            <div className="flex flex-col gap-1">
              <button type="button"
                      onClick={() => openUploadWidget('image', (url) => setForm((p) => ({ ...p, thumbnail_url: url })))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#F4ECD8] text-[#0A1F2E] text-xs hover:bg-[#F9F6EF]"
                      data-testid="lesson-upload-thumbnail">
                <Upload className="w-3.5 h-3.5" /> {form.thumbnail_url ? 'Replace' : 'Upload thumbnail'}
              </button>
              {form.thumbnail_url && (
                <button type="button"
                        onClick={() => setForm((p) => ({ ...p, thumbnail_url: '' }))}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-[#B91C1C] hover:underline"
                        data-testid="lesson-remove-thumbnail">
                  Remove
                </button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-[#888] mt-1">Square or 4:3 works best. Up to 5 MB.</p>
        </div>
        <div>
          <label className="text-[#0A1F2E]/60 text-xs">Order</label>
          <input type="number" min={1} value={form.order}
                 onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value, 10) || 1 }))}
                 className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm"
                 data-testid="lesson-order-input" />
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
          <button type="submit" disabled={saving || !form.cloudinary_video_url}
                  className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold hover:bg-[#2CA8A0] disabled:opacity-50"
                  data-testid="lesson-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ============== MEMBERS TAB (Prompt G) ==============

const VALID_ROLES = ['free_trial', 'community_member', 'client', 'admin', 'expired_trial'];
const CREATE_ROLES = ['free_trial', 'community_member', 'client', 'admin'];
const DESTINATION_CITIES = [
  'Mexico City', 'Tulum', 'Playa del Carmen',
  'Mérida', 'Puerto Vallarta', 'San Miguel de Allende',
];
const SUB_PLANS = [
  { value: '', label: '- none -' },
  { value: 'monthly', label: 'monthly' },
  { value: 'annual', label: 'annual' },
  { value: 'bundle_monthly', label: 'bundle_monthly' },
  { value: 'bundle_annual', label: 'bundle_annual' },
  { value: 'bundle_addon', label: 'bundle_addon' },
];

const MembersTab = () => {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState('');
  const [openActions, setOpenActions] = useState(null);

  // modals / side panels
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);           // user obj
  const [deleting, setDeleting] = useState(null);         // user obj
  const [roleChanging, setRoleChanging] = useState(null); // user obj
  const [notesFor, setNotesFor] = useState(null);         // user obj
  const [messagingFor, setMessagingFor] = useState(null); // user obj

  const load = useCallback(async () => {
    const url = `${API}/api/admin/community/members${q ? `?q=${encodeURIComponent(q)}` : ''}`;
    const r = await fetch(url, { credentials: 'include' });
    if (r.ok) setMembers((await r.json()).members || []);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [load]);

  // Part F: poll every 30s for live table sync
  useEffect(() => {
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
  }, [load]);

  const roleBadge = (role) => ({
    free_trial: 'bg-yellow-600/20 text-yellow-400',
    community_member: 'bg-blue-600/20 text-blue-400',
    client: 'bg-green-600/20 text-green-400',
    admin: 'bg-purple-600/20 text-purple-400',
    expired_trial: 'bg-red-600/20 text-red-400',
  }[role] || 'bg-[#333] text-[#aaa]');

  const sendReset = async (member) => {
    const r = await fetch(`${API}/api/admin/community/members/${member.id}/reset-password`, {
      method: 'POST', credentials: 'include',
    });
    if (r.ok) notifySuccess(`Reset email sent to ${member.email}`);
    else notifyError('Could not send reset email.');
    setOpenActions(null);
  };

  return (
    <div data-testid="admin-members-tab">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <h2 className="text-xl text-white font-light">Members</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-[#666] absolute left-3 top-2.5" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by name or email…"
                   className="bg-[#FFFFFF] border border-[#F4ECD8] text-[#0A1F2E] pl-9 pr-3 py-2 text-sm w-64"
                   data-testid="members-search" />
          </div>
          <button onClick={() => setCreating(true)}
                  className="inline-flex items-center gap-1.5 bg-[#2CA8A0] text-black px-3 py-2 text-sm font-semibold hover:bg-[#2CA8A0] transition"
                  data-testid="create-user-btn">
            <UserPlus className="w-4 h-4" /> Create user
          </button>
        </div>
      </div>

      <div className="bg-[#FFFFFF] border border-[#F4ECD8]">
        <table className="w-full text-sm" data-testid="members-table">
          <thead>
            <tr className="text-left text-[#0A1F2E]/60 text-xs uppercase tracking-wide border-b border-[#F4ECD8]">
              <th className="p-3">Photo</th>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Points</th>
              <th className="p-3">Joined</th>
              <th className="p-3">Trial expiry</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-[#F4ECD8] hover:bg-[#FFFFFF]">
                <td className="p-3">
                  {m.profile_photo_url ? (
                    <img src={m.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#2CA8A0] text-black text-xs font-bold flex items-center justify-center">
                      {(m.first_name || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                </td>
                <td className="p-3 text-white">
                  {[m.first_name, m.last_name].filter(Boolean).join(' ') || '-'}
                </td>
                <td className="p-3 text-[#ccc]">{m.email}</td>
                <td className="p-3"><span className={`px-2 py-0.5 text-xs ${roleBadge(m.role)}`}>{m.role}</span></td>
                <td className="p-3 text-[#ccc]">{m.subscription_plan || '-'}</td>
                <td className="p-3 text-[#2CA8A0]">{m.points || 0}</td>
                <td className="p-3 text-[#888]">{fmtDate(m.created_at)}</td>
                <td className="p-3 text-[#888]">{m.role === 'free_trial' ? fmtDate(m.trial_expiry_date) : '-'}</td>
                <td className="p-3 relative">
                  <div className="flex justify-end">
                    <button onClick={() => setOpenActions(openActions === m.id ? null : m.id)}
                            className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] p-1"
                            data-testid={`member-actions-${m.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                  {openActions === m.id && (
                    <div className="absolute right-3 top-10 z-10 bg-[#0f0f0f] border border-[#F4ECD8] w-52 shadow-xl">
                      <button onClick={() => { setEditing(m); setOpenActions(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF] text-left"
                              data-testid={`action-edit-${m.id}`}>
                        <Edit className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => { setNotesFor(m); setOpenActions(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF] text-left"
                              data-testid={`action-notes-${m.id}`}>
                        <StickyNote className="w-3.5 h-3.5" /> Notes
                      </button>
                      <button onClick={() => { setMessagingFor(m); setOpenActions(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF] text-left"
                              data-testid={`action-message-${m.id}`}>
                        <Send className="w-3.5 h-3.5" /> Message
                      </button>
                      <button onClick={() => { setRoleChanging(m); setOpenActions(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF] text-left">
                        <UserCog className="w-3.5 h-3.5" /> Change role
                      </button>
                      <button onClick={() => sendReset(m)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF] text-left">
                        <KeyRound className="w-3.5 h-3.5" /> Reset password
                      </button>
                      <a href={`/portal/members/${m.id}`} target="_blank" rel="noreferrer"
                         className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ccc] hover:bg-[#FFFFFF]">
                        <UserCircle className="w-3.5 h-3.5" /> View profile
                      </a>
                      <button onClick={() => { setDeleting(m); setOpenActions(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-[#FFFFFF] text-left border-t border-[#F4ECD8]"
                              data-testid={`action-delete-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[#666]">No members found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateUserModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {deleting && (
        <ConfirmDialog
          message="Are you sure you want to delete this user? This will permanently delete their account, progress, posts, comments, and notifications."
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const r = await fetch(`${API}/api/admin/community/members/${deleting.id}`, {
              method: 'DELETE', credentials: 'include',
            });
            setDeleting(null);
            if (r.ok) load();
          }}
        />
      )}
      {notesFor && (
        <NotesPanel user={notesFor} onClose={() => setNotesFor(null)} />
      )}
      {messagingFor && (
        <MessageModal user={messagingFor} onClose={() => setMessagingFor(null)} />
      )}
      {roleChanging && (
        <Modal title="Change role" onClose={() => setRoleChanging(null)}>
          <div className="space-y-3">
            <p className="text-[#ccc] text-sm">
              Changing role for <b className="text-white">{roleChanging.email}</b>
            </p>
            <select id="new-role-select" defaultValue={roleChanging.role}
                    className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm"
                    data-testid="role-change-select">
              {VALID_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRoleChanging(null)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
              <button
                onClick={async () => {
                  const v = document.getElementById('new-role-select').value;
                  const r = await fetch(`${API}/api/admin/community/members/${roleChanging.id}/role`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    credentials: 'include', body: JSON.stringify({ role: v }),
                  });
                  setRoleChanging(null);
                  if (r.ok) load();
                }}
                className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold"
                data-testid="role-change-save">
                Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ============== CREATE USER MODAL ==============

const CreateUserModal = ({ onClose, onCreated }) => {
  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', password: '',
    role: 'client', destination_city: '', current_city: '',
    send_welcome_email: true,
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setErr(null); setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/community/members`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(f),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not create user');
      onCreated();
    } catch (e) {
      setErr(e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Create user" onClose={onClose} testId="create-user-modal">
      <div className="space-y-3">
        {err && <p className="text-red-400 text-xs" data-testid="create-user-error">{err}</p>}
        <FormInput label="First name *" value={f.first_name} onChange={(v) => setF({ ...f, first_name: v })} testId="cu-first-name" />
        <FormInput label="Last name *"  value={f.last_name}  onChange={(v) => setF({ ...f, last_name: v })}  testId="cu-last-name" />
        <FormInput label="Email *" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="cu-email" />
        <FormInput label="Password (min 8 chars) *" type="password" value={f.password}
                   onChange={(v) => setF({ ...f, password: v })} testId="cu-password" />
        <FormSelect label="Role *" value={f.role} onChange={(v) => setF({ ...f, role: v })}
                    options={CREATE_ROLES.map((r) => ({ value: r, label: r }))}
                    testId="cu-role" />
        <FormSelect label="Destination city" value={f.destination_city}
                    onChange={(v) => setF({ ...f, destination_city: v })}
                    options={[{ value: '', label: '- select -' },
                              ...DESTINATION_CITIES.map((c) => ({ value: c, label: c }))]}
                    testId="cu-destination" />
        <FormInput label="Current city" value={f.current_city}
                   onChange={(v) => setF({ ...f, current_city: v })} testId="cu-current-city" />
        <label className="flex items-center gap-2 pt-1" data-testid="cu-send-welcome">
          <input type="checkbox" checked={f.send_welcome_email}
                 onChange={(e) => setF({ ...f, send_welcome_email: e.target.checked })} />
          <span className="text-sm text-[#ccc]">Send welcome email</span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
          <button onClick={submit} disabled={saving}
                  className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold disabled:opacity-60"
                  data-testid="cu-submit">
            {saving ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ============== EDIT USER MODAL ==============

const EditUserModal = ({ user, onClose, onSaved }) => {
  const [f, setF] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    role: user.role || 'client',
    current_city: user.current_city || '',
    destination_city: user.destination_city || '',
    subscription_plan: user.subscription_plan || '',
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setErr(null); setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/community/members/${user.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(f),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Could not update user');
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit ${user.email}`} onClose={onClose} testId="edit-user-modal">
      <div className="space-y-3">
        {err && <p className="text-red-400 text-xs">{err}</p>}
        <FormInput label="First name" value={f.first_name} onChange={(v) => setF({ ...f, first_name: v })} testId="eu-first-name" />
        <FormInput label="Last name"  value={f.last_name}  onChange={(v) => setF({ ...f, last_name: v })} testId="eu-last-name" />
        <FormInput label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="eu-email" />
        <FormSelect label="Role" value={f.role} onChange={(v) => setF({ ...f, role: v })}
                    options={VALID_ROLES.map((r) => ({ value: r, label: r }))} testId="eu-role" />
        <FormInput label="Current city" value={f.current_city} onChange={(v) => setF({ ...f, current_city: v })} testId="eu-current-city" />
        <FormSelect label="Destination city" value={f.destination_city}
                    onChange={(v) => setF({ ...f, destination_city: v })}
                    options={[{ value: '', label: '- none -' },
                              ...DESTINATION_CITIES.map((c) => ({ value: c, label: c }))]}
                    testId="eu-destination" />
        <FormSelect label="Subscription plan" value={f.subscription_plan}
                    onChange={(v) => setF({ ...f, subscription_plan: v })}
                    options={SUB_PLANS} testId="eu-plan" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
          <button onClick={submit} disabled={saving}
                  className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold disabled:opacity-60"
                  data-testid="eu-submit">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ============== NOTES SIDE PANEL ==============

const NotesPanel = ({ user, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/admin/community/members/${user.id}/notes`, { credentials: 'include' });
    if (r.ok) setNotes((await r.json()).notes || []);
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/community/members/${user.id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ note_text: trimmed.slice(0, 1000) }),
      });
      if (r.ok) { setText(''); load(); }
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    const r = await fetch(`${API}/api/admin/community/members/${user.id}/notes/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (r.ok) load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} data-testid="notes-panel">
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[420px] bg-[#0f0f0f] border-l border-[#F4ECD8] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#F4ECD8]">
          <div>
            <h3 className="text-[#0A1F2E] font-medium">Notes</h3>
            <p className="text-[#0A1F2E]/60 text-xs">{user.email}</p>
          </div>
          <button onClick={onClose} className="text-[#0A1F2E]/60 hover:text-[#0A1F2E] min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="notes-list">
          {notes.map((n) => (
            <div key={n.note_id} className="bg-[#FFFFFF] border border-[#F4ECD8] p-3" data-testid={`note-${n.note_id}`}>
              <p className="text-[#eaeaea] text-sm whitespace-pre-wrap break-words">{n.note_text}</p>
              <div className="flex items-center justify-between mt-2">
                <p className="text-[#666] text-xs flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {fmtDate(n.created_at, true)}
                </p>
                <button onClick={() => remove(n.note_id)} className="text-red-400 hover:underline text-xs"
                        data-testid={`note-delete-${n.note_id}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
          {notes.length === 0 && (
            <p className="text-[#666] text-sm text-center py-6">No notes yet.</p>
          )}
        </div>

        <div className="p-4 border-t border-[#F4ECD8]">
          <textarea value={text} onChange={(e) => setText(e.target.value)}
                    rows={3} maxLength={1000}
                    placeholder="Add a note about this user…"
                    className="w-full bg-[#FFFFFF] border border-[#F4ECD8] text-[#0A1F2E] text-sm p-3 resize-none focus:outline-none"
                    data-testid="notes-textarea" />
          <div className="flex justify-between items-center mt-2">
            <span className="text-[#666] text-xs">{text.length}/1000</span>
            <button onClick={add} disabled={saving || !text.trim()}
                    className="px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold disabled:opacity-60"
                    data-testid="notes-save">
              {saving ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== MESSAGE MODAL ==============

const MessageModal = ({ user, onClose }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/community/members/${user.id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ message_text: trimmed.slice(0, 2000) }),
      });
      if (r.ok) { setSent(true); setTimeout(onClose, 1200); }
    } finally { setSending(false); }
  };

  return (
    <Modal title={`Message ${user.email}`} onClose={onClose} testId="message-modal">
      <div className="space-y-3">
        {sent ? (
          <p className="text-[#2CA8A0] text-sm" data-testid="message-sent-confirmation">Message sent.</p>
        ) : (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)}
                      rows={5} maxLength={2000}
                      placeholder="Write a message to this user…"
                      className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white text-sm p-3 resize-none focus:outline-none"
                      data-testid="message-textarea" />
            <div className="flex justify-between items-center">
              <span className="text-[#666] text-xs">{text.length}/2000</span>
              <button onClick={send} disabled={sending || !text.trim()}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#2CA8A0] text-black font-semibold disabled:opacity-60"
                      data-testid="message-send-btn">
                <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

// ============== REUSABLE FORM INPUTS ==============

const FormInput = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <label className="block text-[#0A1F2E]/60 text-xs uppercase tracking-wide mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm focus:outline-none"
           data-testid={testId} />
  </div>
);

const FormSelect = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-[#0A1F2E]/60 text-xs uppercase tracking-wide mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-[#0f0f0f] border border-[#F4ECD8] text-white px-3 py-2 text-sm focus:outline-none"
            data-testid={testId}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

// ============== POSTS MODERATION TAB ==============

const PostsTab = () => {
  const [posts, setPosts] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/admin/community/posts`, { credentials: 'include' });
    if (r.ok) setPosts((await r.json()).posts || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (post_id) => {
    await fetch(`${API}/api/admin/community/posts/${post_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    setDeleting(null);
    load();
  };

  return (
    <div data-testid="admin-posts-tab">
      <h2 className="text-xl text-white font-light mb-4">Posts moderation</h2>
      <div className="bg-[#FFFFFF] border border-[#F4ECD8] overflow-x-auto">
        <table className="w-full text-sm" data-testid="posts-mod-table">
          <thead>
            <tr className="text-left text-[#0A1F2E]/60 text-xs uppercase tracking-wide border-b border-[#F4ECD8]">
              <th className="p-3">Author</th>
              <th className="p-3">Channel</th>
              <th className="p-3">Content</th>
              <th className="p-3">Likes</th>
              <th className="p-3">Comments</th>
              <th className="p-3">Created</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.post_id} className="border-b border-[#F4ECD8] hover:bg-[#FFFFFF]">
                <td className="p-3 text-white">{p.author_first_name}</td>
                <td className="p-3 text-[#ccc]">{p.channel}</td>
                <td className="p-3 text-[#ccc] max-w-md">
                  {(p.content || '').slice(0, 80)}{(p.content || '').length > 80 ? '…' : ''}
                </td>
                <td className="p-3 text-[#ccc]">{p.likes_count}</td>
                <td className="p-3 text-[#ccc]">{p.comment_count}</td>
                <td className="p-3 text-[#888]">{fmtDate(p.created_at, true)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setDeleting(p)} className="text-red-400 hover:underline text-xs"
                          data-testid={`delete-post-${p.post_id}`}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[#666]">No posts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {deleting && (
        <ConfirmDialog
          message="Are you sure you want to delete this post and all its comments?"
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting.post_id)}
        />
      )}
    </div>
  );
};

// ============== MAIN COMMUNITY SECTION ==============

const AdminCommunitySection = () => {
  const [activeSub, setActiveSub] = useState('modules');
  const subs = [
    { id: 'modules', icon: BookOpen, label: 'Modules' },
    { id: 'members', icon: Users, label: 'Members' },
    { id: 'posts', icon: MessageSquare, label: 'Posts moderation' },
  ];
  return (
    <div>
      <div className="flex gap-1 mb-5 border-b border-[#F4ECD8]">
        {subs.map((s) => {
          const Icon = s.icon;
          const active = activeSub === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSub(s.id)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 transition ${
                active
                  ? 'text-[#2CA8A0] border-[#2CA8A0]'
                  : 'text-[#888] border-transparent hover:text-white'
              }`}
              data-testid={`admin-community-sub-${s.id}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>
      {activeSub === 'modules' && <ModulesTab />}
      {activeSub === 'members' && <MembersTab />}
      {activeSub === 'posts' && <PostsTab />}
    </div>
  );
};

export default AdminCommunitySection;
